import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import logo529 from "@/assets/logo-529.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const navLinks = [
    { href: "#accueil", label: "Accueil" },
    { href: "#demo", label: "Démo" },
    { href: "#process", label: "Process" },
    { href: "#offres", label: "Offres" },
    { href: "#faq", label: "FAQ" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Track active section - only highlight when actually in section bounds
      const sections = ["accueil", "demo", "process", "offres", "faq"];
      const scrollPosition = window.scrollY + 200;
      let foundSection: string | null = null;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          // Check if we're within this section's bounds
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            foundSection = `#${section}`;
            break;
          }
        }
      }
      
      setActiveSection(foundSection);
    };
    
    // Initial check
    handleScroll();
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 py-3 sm:py-4"
    >
      <div className="container mx-auto px-3 sm:px-4">
        <motion.div 
          className="flex items-center justify-between"
          initial={false}
          animate={{
            maxWidth: isScrolled ? "56rem" : "100%",
            backgroundColor: isScrolled ? "hsl(var(--background) / 0.7)" : "transparent",
            borderWidth: isScrolled ? "1px" : "0px",
            borderColor: isScrolled ? "hsl(var(--foreground) / 0.1)" : "transparent",
            borderRadius: isScrolled ? "9999px" : "0px",
            paddingLeft: isScrolled ? "1rem" : "0px",
            paddingRight: isScrolled ? "1rem" : "0px",
            paddingTop: isScrolled ? "0.5rem" : "0px",
            paddingBottom: isScrolled ? "0.5rem" : "0px",
            boxShadow: isScrolled 
              ? "0 10px 15px -3px rgb(0 0 0 / 0.05), 0 4px 6px -4px rgb(0 0 0 / 0.05)" 
              : "0 0 0 0 transparent",
            marginLeft: "auto",
            marginRight: "auto",
          }}
          transition={{
            duration: 0.4,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          style={{
            backdropFilter: isScrolled ? "blur(24px)" : "blur(0px)",
            WebkitBackdropFilter: isScrolled ? "blur(24px)" : "blur(0px)",
          }}
        >
          {/* Logo */}
          <a 
            href="#accueil" 
            onClick={(e) => { e.preventDefault(); scrollToSection("#accueil"); }} 
            className="flex items-center flex-shrink-0"
          >
            <motion.img 
              src={logo529} 
              alt="Agence 529" 
              width={44}
              height={44}
              className="w-auto hover:scale-105 transition-transform"
              initial={false}
              animate={{
                height: isScrolled ? "2rem" : "2.75rem",
              }}
              transition={{
                duration: 0.3,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            />
          </a>

          {/* Desktop Navigation - Center Pill with Sliding Indicator */}
          <div className="hidden md:flex items-center absolute left-1/2 -translate-x-1/2">
            <motion.div 
              className="relative flex items-center gap-1 px-1.5 lg:px-2 py-1.5 rounded-full"
              initial={false}
              animate={{
                backgroundColor: isScrolled 
                  ? "hsl(var(--foreground) / 0.05)" 
                  : "hsl(var(--foreground) / 0.05)",
                borderWidth: isScrolled ? "0px" : "1px",
                borderColor: isScrolled ? "transparent" : "hsl(var(--foreground) / 0.1)",
              }}
              transition={{
                duration: 0.3,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              style={{
                backdropFilter: isScrolled ? "none" : "blur(16px)",
                WebkitBackdropFilter: isScrolled ? "none" : "blur(16px)",
              }}
            >
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className={`relative z-10 px-4 lg:px-5 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                    activeSection === link.href
                      ? "text-primary-foreground"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {/* Sliding pill background */}
                  {activeSection === link.href && (
                    <motion.div
                      layoutId="activeNavPill"
                      className="absolute inset-0 bg-primary rounded-full shadow-md"
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30
                      }}
                    />
                  )}
                  <span className="relative z-10">{link.label}</span>
                </button>
              ))}
            </motion.div>
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <motion.div
              initial={false}
              animate={{
                scale: isScrolled ? 0.92 : 1,
              }}
              transition={{
                duration: 0.3,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <Button 
                size="lg"
                onClick={() => scrollToSection("#offres")}
                className="rounded-full px-6 text-sm font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
              >
                Démarrer
              </Button>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground hover:bg-foreground/10 rounded-full transition-colors flex-shrink-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </motion.div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden"
            >
              <div className={`py-3 flex flex-col gap-0.5 mt-2 ${
                isScrolled ? "" : "bg-background/80 backdrop-blur-xl border border-foreground/10 rounded-2xl px-2"
              }`}>
                {navLinks.map((link, index) => (
                  <motion.button
                    key={link.href}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`text-left text-sm font-medium py-2.5 px-3 rounded-xl transition-colors ${
                      activeSection === link.href 
                        ? "bg-primary/10 text-primary" 
                        : "text-foreground hover:bg-muted/50"
                    }`}
                    onClick={() => scrollToSection(link.href)}
                  >
                    {link.label}
                  </motion.button>
                ))}
                <Button 
                  className="rounded-full mt-2" 
                  size="sm"
                  onClick={() => scrollToSection("#offres")}
                >
                  Démarrer
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
